package dev.lingvo.site.pages.app.settings.billing

import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.layout.Layout
import dev.lingvo.shared.BillingInterval
import dev.lingvo.shared.BillingOverviewDto
import dev.lingvo.shared.BillingPlanOfferDto
import dev.lingvo.shared.Money
import dev.lingvo.shared.Plan
import dev.lingvo.shared.Role
import dev.lingvo.site.api.ApiClient
import dev.lingvo.site.api.loadAsync
import dev.lingvo.site.components.marketing.formatThousands
import dev.lingvo.site.ds.LAlert
import dev.lingvo.site.ds.LAlertVariant
import dev.lingvo.site.ds.LAsyncContent
import dev.lingvo.site.ds.LButton
import dev.lingvo.site.ds.LButtonVariant
import dev.lingvo.site.ds.LLinkButton
import dev.lingvo.site.ds.LMuted
import dev.lingvo.site.ds.PageHeader
import dev.lingvo.site.ds.Space
import dev.lingvo.site.i18n.t
import dev.lingvo.site.i18n.tPlural
import dev.lingvo.site.i18n.tr
import dev.lingvo.site.i18n.trf
import dev.lingvo.site.pages.app.projects.slug.settings.settingsOperationResult
import dev.lingvo.site.state.WorkspaceSelection
import kotlinx.browser.window
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.dom.Div

/**
 * What each plan costs and what it actually changes.
 *
 * Priced **per billable seat**, and the page says so with this workspace's own number rather than a
 * table somebody has to do arithmetic against: "$19 × 3 owners and admins = $57 a month" is the
 * sentence people are trying to work out, and translators being free is the part they do not
 * believe until it is stated.
 *
 * Admins see everything and can start nothing. An admin who could check out would be committing
 * somebody else's card to a recurring charge.
 */
@Page("/app/settings/billing/plans")
@Layout(".components.layouts.AppLayout")
@Composable
fun PlansPage() {
    val scope = rememberCoroutineScope()
    val workspace = WorkspaceSelection.current()
    val slug = workspace?.slug
    // The role in the workspace this page is acting on, not the best role held anywhere. With
    // `strongestRole()` somebody who owns one workspace and merely views another was offered
    // checkout for the one they were looking at, and found out it was refused after paying it
    // attention — the authority shown and the authority exercised have to be the same workspace's.
    val role = workspace?.role
    var busy by remember { mutableStateOf<Plan?>(null) }
    var message by remember { mutableStateOf<String?>(null) }
    var refreshKey by remember { mutableStateOf(0) }

    val state = loadAsync(slug, refreshKey) { slug?.let { ApiClient.workspaceBilling(it) } }

    Column(Modifier.fillMaxWidth().gap(Space.XL)) {
        PageHeader(t("app.settings.billing.plans.plans", "Plans"), t("app.settings.billing.plans.per_owner_or_admin_translators_and", "Per owner or admin. Translators and viewers are free on every plan."))

        // `live`, because this banner exists only to report a checkout the reader just started. A
        // failed checkout that announces nothing leaves somebody waiting for a payment screen that
        // is never going to open.
        message?.let {
            LAlert(
                t("app.settings.billing.plans.that_didnt_work", "That didn't work"),
                it,
                variant = LAlertVariant.WARNING,
                live = true,
            )
        }

        LAsyncContent(
            state,
            rows = 3,
            loadingLabel = t("app.settings.billing.plans.loading_plans", "Loading plans"),
            // A failed read leaves plan and payment status unknown. Retry the same billing read
            // before showing offers or checkout actions.
            failure = {
                LAlert(
                    t("app.settings.billing.plans.we_cant_read_your_plan_right", "We can't read your plan right now"),
                    t("app.settings.billing.plans.unavailable_status", "Refresh billing to check your latest plan and payment status."),
                    variant = LAlertVariant.WARNING,
                    action = {
                        LButton(
                            t("app.settings.billing.plans.retry", "Retry"),
                            { refreshKey++ },
                            variant = LButtonVariant.SECONDARY,
                        )
                    },
                )
            },
            gap = Space.XL,
        ) { billing ->
            val seats = billing?.seats?.held ?: 1
            val current = billing?.plan ?: Plan.FREE

            if (role != Role.OWNER) {
                LAlert(
                    t("app.settings.billing.plans.only_an_owner_can_change_the", "Only an owner can change the plan"),
                    t("app.settings.billing.plans.you_can_see_everything_here_starting_2", "You can see everything here. Starting a subscription commits a card, and " +
                        "that is the owner's decision to make."),
                    variant = LAlertVariant.INFO,
                )
            }

            if (billing?.pendingCheckoutStartedAt != null) {
                LAlert(
                    t("app.settings.billing.plans.checkout_pending", "A checkout is awaiting confirmation"),
                    t("app.settings.billing.plans.checkout_pending_help", "Finish the checkout you already opened, or check its status. Starting another could charge you twice."),
                    variant = LAlertVariant.INFO,
                    action = {
                        LLinkButton(
                            label = t("app.settings.billing.plans.check_checkout", "Check checkout status"),
                            path = "/app/settings/billing/confirming",
                            variant = LButtonVariant.SECONDARY,
                        )
                    },
                )
            }

            if (billing?.hasSubscription == true) {
                LMuted(t("app.settings.billing.plans.review_the_payment_providers_estimate_before", "Review the payment provider's estimate before changing your existing subscription. Team is available by request."))
                if (role == Role.OWNER && slug != null) {
                    androidx.compose.runtime.key(slug, role, current, seats) {
                        PlanChangeFlow(
                            current = current,
                            targets = Plan.entries.filter { target ->
                                target != current && target != Plan.FREE &&
                                    billing.planOffers.singleOrNull { it.plan == target }?.available == true
                            },
                            previewChange = { ApiClient.previewWorkspacePlanChange(slug, it) },
                            confirmChange = { ApiClient.confirmWorkspacePlanChange(slug, it) },
                        )
                    }
                }
            } else if (billing?.checkoutAvailable == false) {
                LAlert(
                    t("app.settings.billing.plans.paid_plans_are_not_available_on", "Paid plans are not available on this deployment yet"),
                    t("app.settings.billing.plans.everything_below_is_real_there_is", "Everything below is real; there is just nothing to check out against."),
                    variant = LAlertVariant.INFO,
                )
            }

            Div(planGridModifier().toAttrs()) {
                Plan.entries.forEach { plan ->
                    PlanCard(
                        plan = plan,
                        seats = seats,
                        offer = billing?.planOffers?.singleOrNull { it.plan == plan },
                        current = plan == current,
                        canAct = canStartCheckout(role, plan, current, billing),
                        enabled = busy == null,
                        busy = busy == plan,
                        onChoose = {
                            scope.launch {
                                busy = plan
                                // `settingsOperationResult`, never `runCatching`: `runCatching`
                                // catches the `CancellationException` this scope is torn down with
                                // when the reader navigates away mid-checkout, and turns it into
                                // "That didn't work" — a payment failure recorded for a checkout
                                // that was never refused. It lands on state the departed
                                // composition no longer reads, so nothing is shown for it today;
                                // the reason to stop swallowing it is that a cancellation is not a
                                // result and has to keep unwinding.
                                message = settingsOperationResult<String?> {
                                    val checkout = ApiClient.startWorkspaceCheckout(slug!!, plan)
                                    window.location.href = checkout.url
                                    null
                                }.getOrElse { it.message }
                                busy = null
                            }
                        },
                    )
                }
            }

            CommonPlanInclusions()
            CreditExamples()
        }
    }
}

/**
 * Whether a checkout button on this card can do anything at all.
 *
 * **Every clause here mirrors a refusal the server already makes**, because a button that exists
 * only to produce an error is worse than no button: the error it produced named the billing page as
 * the place to change plans, and that page offers a link back to this one and a cancel button. A
 * paying owner who wanted to upgrade was walked in a circle whose only exit was cancelling — which
 * either costs them the rest of the period or walks them into the double-subscription race.
 *
 * Admins read; owners act. An admin who could check out would be committing somebody else's card.
 */
internal fun canStartCheckout(
    role: Role?,
    plan: Plan,
    current: Plan,
    billing: BillingOverviewDto?,
): Boolean = role == Role.OWNER &&
    plan != current &&
    plan != Plan.FREE &&
    billing?.hasSubscription != true &&
    billing?.checkoutAvailable == true &&
    billing.pendingCheckoutStartedAt == null &&
    billing.planOffers.singleOrNull { it.plan == plan }?.available == true

/** Use the supplied offer, including its currency and billing interval. Missing is not a price. */
internal fun planOfferPriceLine(offer: BillingPlanOfferDto?, seats: Int): String {
    if (offer == null) return tr("app.settings.billing.plans.offer_unavailable", "Pricing is unavailable. Reload before choosing a plan.")
    if (offer.plan == Plan.FREE) return tr("app.settings.billing.plans.free", "Free")
    val total = Money.of(offer.pricePerSeat.amountMinor * seats, offer.pricePerSeat.currency)
    return if (offer.interval == BillingInterval.MONTH) {
        trf(
            "app.settings.billing.plans.monthly_offer",
            "{price} per owner or admin · {seats} today = {total} a month, before tax.",
            "price" to offer.pricePerSeat.display,
            "seats" to seats.toString(),
            "total" to total.display,
        )
    } else {
        trf(
            "app.settings.billing.plans.annual_offer",
            "{price} per owner or admin · {seats} today = {total} a year, before tax.",
            "price" to offer.pricePerSeat.display,
            "seats" to seats.toString(),
            "total" to total.display,
        )
    }
}

/**
 * What a plan includes, as four whole sentences rather than a `buildString`.
 *
 * The run this replaces translated two of its four clauses through `tPlural` and glued the other
 * two on as English literals — every card read "3 Projekte. Unlimited translators and viewers." —
 * and it held the ". " between the clauses outside the catalogue, so a locale that ends a clause
 * with "。", or joins two of them differently, had no way to reach the join. Each clause now
 * carries its own terminal punctuation and no fragment is assembled with `+`.
 */
internal fun planSummaryLine(plan: Plan): String = listOf(
    trf(
        "app.settings.billing.plans.credits_machine_translation_credits_per_seat_each",
        "{credits} machine-translation credits per seat each month.",
        "credits" to formatThousands(plan.creditsPerSeat),
    ),
    plan.maxProjects
        ?.let {
            tPlural(
                "app.settings.billing.plans.count.project_limit",
                it,
                "{count} project.",
                "{count} projects.",
            )
        }
        ?: tr("app.settings.billing.plans.unlimited_projects", "Unlimited projects."),
    plan.maxBillableSeats
        ?.let {
            tPlural(
                "app.settings.billing.plans.count.owner_admin_seat_limit",
                it,
                "{count} owner or admin seat.",
                "{count} owner or admin seats.",
            )
        }
        ?: tr(
            "app.settings.billing.plans.as_many_owners_and_admins_as",
            "As many owners and admins as you need.",
        ),
    tr(
        "app.settings.billing.plans.unlimited_translators_and_viewers",
        "Unlimited translators and viewers.",
    ),
).joinToString(" ")
